// The client-side send ceiling of the feedback form, written ONCE. The server
// enforces the same 10 per hour (apps/api/src/routes/feedback.ts); this only
// gives an immediate "wait a bit" instead of a 429 round-trip on each tap, and
// stops a kid mashing Send. The window and the ceiling live here; WHERE the
// timestamps are kept is the mount's: AsyncStorage on mobile, localStorage on
// web. Both already speak getItem/setItem, so the seam is that shape.

const WINDOW_MS = 60 * 60 * 1000;
const CEILING = 10;

/** The slice of AsyncStorage / Storage the limiter needs (sync or async). */
export type RateLimitStorage = {
  getItem(key: string): Promise<string | null> | string | null;
  setItem(key: string, value: string): Promise<void> | void;
};

export type RateLimitCheck = { allowed: boolean; remaining: number };

export function createFeedbackRateLimiter(
  storage: RateLimitStorage,
  key: string,
): () => Promise<RateLimitCheck> {
  const load = async (): Promise<number[]> => {
    try {
      const raw = await storage.getItem(key);
      if (raw === null) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((v): v is number => typeof v === "number");
    } catch {
      return [];
    }
  };
  const persist = async (ts: number[]): Promise<void> => {
    try {
      await storage.setItem(key, JSON.stringify(ts));
    } catch {
      // Best-effort.
    }
  };
  return async () => {
    const now = Date.now();
    const cutoff = now - WINDOW_MS;
    const recent = (await load()).filter((t) => t > cutoff);
    if (recent.length >= CEILING) {
      await persist(recent);
      return { allowed: false, remaining: 0 };
    }
    recent.push(now);
    await persist(recent);
    return { allowed: true, remaining: CEILING - recent.length };
  };
}
